import EditOutlined from '@mui/icons-material/EditOutlined';
import { Stack } from '@mui/material';
import Typography from '@mui/material/Typography';
import { GridActionsCellItem, ValueOptions } from '@mui/x-data-grid-pro';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	AgreementBusinessModel,
	AgreementServiceType,
	ChangedByInfoDto,
	ServiceAgreementDto,
	ServiceAgreementStatus,
} from 'api';
import {
	agreementBusinessModelToTranslationMapping,
	agreementServiceTypeToTranslationMapping,
	serviceAgreementStatusToTranslationMapping,
} from 'api/responses/models/Unit/ServiceAgreementDto';
import { AreaName } from 'domain/constants';
import { DataGridToolbarWithCsvExport } from 'library/components/controls/datagrid/DataGridToolbar';
import {
	TypeSafeColDef,
	VolvoDataGrid,
} from 'library/components/controls/datagrid/VolvoDataGrid';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import { formatValueWithSecondInBracket } from 'library/formatters/StringFormatter';
import { OperationType } from 'library/models/enums/OperationType';
import { checkUserAccess } from 'main/auth/checkUserAccess';

interface ServiceAgreementHistoryProps {
	data: ServiceAgreementDto[];
	onEdit: (agreement: ServiceAgreementDto) => void;
}

export const ServiceAgreementLog = ({
	data,
	onEdit,
}: ServiceAgreementHistoryProps): React.JSX.Element => {
	const { t } = useTranslation(['unitOverview', 'common', 'commonButton']);
	const queryClient = useQueryClient();
	const canEditServiceAgreements = useMemo(
		() =>
			checkUserAccess(queryClient, {
				areaName: AreaName.UnitConfiguration_EditServiceAgreement,
				permission: OperationType.Write,
			}),
		[queryClient]
	);

	const columns = useMemo(() => {
		const tempColumns: TypeSafeColDef<ServiceAgreementDto>[] = [
			{
				field: 'status',
				headerName: t('unitOverview:status-header'),
				type: 'singleSelect',
				minWidth: 100,
				flex: 1,
				valueOptions: Object.keys(
					serviceAgreementStatusToTranslationMapping
				).map((value): ValueOptions => ({
					value,
					label: t(
						serviceAgreementStatusToTranslationMapping[
							value as ServiceAgreementStatus
						]
					),
				})),
				valueFormatter: (value) =>
					t(serviceAgreementStatusToTranslationMapping[value]),
			},
			{
				field: 'startDate',
				headerName: t('unitOverview:service-agreement-start-date'),
				type: 'date',
				minWidth: 100,
				flex: 1,
				valueFormatter: (value) => getLocalizedDateFormat(value),
			},
			{
				field: 'endDate',
				headerName: t('common:end-date'),
				type: 'date',
				minWidth: 100,
				flex: 1,
				valueFormatter: (value) => getLocalizedDateFormat(value),
			},
			{
				field: 'reference',
				headerName: t('unitOverview:service-agreement-reference'),
				minWidth: 120,
				flex: 1,
			},
			{
				field: 'startHour',
				headerName: t('unitOverview:service-agreement-start-hour'),
				type: 'number',
				minWidth: 100,
				flex: 1,
			},
			{
				field: 'maxHour',
				headerName: t('unitOverview:service-agreement-end-max-hour'),
				type: 'number',
				minWidth: 120,
				flex: 1,
			},
			{
				field: 'businessModel',
				headerName: t('unitOverview:service-agreement-business-model'),
				type: 'singleSelect',
				minWidth: 120,
				flex: 1,
				valueOptions: Object.keys(
					agreementBusinessModelToTranslationMapping
				).map((value): ValueOptions => ({
					value,
					label: t(
						agreementBusinessModelToTranslationMapping[
							value as AgreementBusinessModel
						]
					),
				})),
				valueFormatter: (value) =>
					typeof value === 'string' && value
						? t(
								agreementBusinessModelToTranslationMapping[
									value as AgreementBusinessModel
								]
							)
						: '',
			},
			{
				field: 'serviceType',
				headerName: t('unitOverview:service-agreement-service-type'),
				type: 'singleSelect',
				minWidth: 120,
				flex: 1,
				valueOptions: Object.keys(
					agreementServiceTypeToTranslationMapping
				).map((value): ValueOptions => ({
					value,
					label: t(
						agreementServiceTypeToTranslationMapping[
							value as AgreementServiceType
						]
					),
				})),
				valueFormatter: (value) =>
					typeof value === 'string' && value
						? t(
								agreementServiceTypeToTranslationMapping[
									value as AgreementServiceType
								]
							)
						: '',
			},
			{
				field: 'validatedByVolvoPenta',
				headerName: t(
					'unitOverview:service-agreement-validated-by-volvo-penta'
				),
				type: 'boolean',
				minWidth: 120,
				flex: 1,
			},
			{
				field: 'preventiveMaintenance',
				headerName: t(
					'unitOverview:service-agreement-preventive-maintenance'
				),
				type: 'boolean',
				minWidth: 120,
				flex: 1,
			},
			{
				field: 'genuineOil',
				headerName: t('unitOverview:service-agreement-genuine-oil'),
				type: 'boolean',
				minWidth: 120,
				flex: 1,
			},
			{
				field: 'preventiveRepair',
				headerName: t(
					'unitOverview:service-agreement-preventive-repair'
				),
				type: 'boolean',
				minWidth: 120,
				flex: 1,
			},
			{
				field: 'additionalContent',
				headerName: t(
					'unitOverview:service-agreement-additional-information'
				),
				minWidth: 130,
				flex: 1,
			},
			{
				field: 'tcoUsed',
				headerName: t(
					'unitOverview:service-agreement-service-calculator'
				),
				type: 'boolean',
				minWidth: 120,
				flex: 1,
			},
			{
				field: 'createdByInfo',
				headerName: t('unitOverview:service-agreement-created'),
				display: 'flex',
				minWidth: 140,
				flex: 1,
				valueGetter: (value) =>
					formatValueWithSecondInBracket(
						(value as ChangedByInfoDto)?.changedByUserId,
						getLocalizedDateFormat(
							(value as ChangedByInfoDto)?.changedOnDate
						)
					),
				renderCell: ({ row }) => (
					<Stack>
						<Typography variant="body2">
							{row.createdByInfo.changedByUserId}
						</Typography>
						{row.createdByInfo.changedOnDate && (
							<Typography
								variant="caption"
								sx={{
									color: 'text.secondary',
								}}>
								(
								{getLocalizedDateFormat(
									row.createdByInfo.changedOnDate
								)}
								)
							</Typography>
						)}
					</Stack>
				),
			},
			{
				field: 'businessPartnerName',
				headerName: t(
					'unitOverview:responsible-business-partner-label'
				),
				display: 'flex',
				minWidth: 150,
				flex: 1,
				renderCell: ({ row }) =>
					formatValueWithSecondInBracket(
						row.businessPartnerName,
						row.businessPartnerId
					),
			},
			{
				field: 'changedByInfo',
				headerName: t('common:updated-by'),
				minWidth: 140,
				display: 'flex',
				flex: 1,
				valueGetter: (value) =>
					formatValueWithSecondInBracket(
						(value as ChangedByInfoDto)?.changedByUserId,
						getLocalizedDateFormat(
							(value as ChangedByInfoDto)?.changedOnDate
						)
					),
				renderCell: ({ row }) => (
					<Stack>
						<Typography variant="body2">
							{row.changedByInfo.changedByUserId}
						</Typography>
						<Typography
							variant="caption"
							sx={{
								color: 'text.secondary',
							}}>
							(
							{getLocalizedDateFormat(
								row.changedByInfo.changedOnDate
							)}
							)
						</Typography>
					</Stack>
				),
			},
		];

		if (canEditServiceAgreements) {
			tempColumns.push({
				field: 'actions',
				type: 'actions',
				minWidth: 80,
				flex: 1,
				getActions: ({ row: agreement }) => {
					if (
						agreement.status === 'Active' ||
						agreement.status === 'Planned'
					) {
						return [
							<GridActionsCellItem
								key="edit"
								icon={<EditOutlined />}
								label={t('commonButton:edit')}
								onClick={() => onEdit(agreement)}
								showInMenu={false}
							/>,
						];
					}
					return [];
				},
			});
		}
		return tempColumns;
	}, [canEditServiceAgreements, t, onEdit]);

	return (
		<VolvoDataGrid
			density="comfortable"
			columns={columns}
			rows={data}
			showToolbar
			slots={{ toolbar: DataGridToolbarWithCsvExport }}
			initialState={{
				sorting: { sortModel: [{ field: 'endDate', sort: 'desc' }] },
			}}
			isLoading={false}
		/>
	);
};
